import { useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { format } from 'date-fns';
import { MdKeyboardArrowRight, MdKeyboardArrowDown } from 'react-icons/md';
import styles from './style/EnterpriseList.module.scss';
import TextField from '../Utilities/TextField';
import AlertSwitch from '../Utilities/AlertSwitch';
import Datepicker from '../Utilities/Datepicker/Datepicker';
import Tips from '../Utilities/Tips';
import { selectPermissionSettings } from '../../store/slice/permissionSettings';
import {
  computeUsageDefaults,
  computeUsageQuota,
  validateUsage,
  isUsageDirty,
  STORAGE_GB_PER_SEAT,
  TRAFFIC_GB_PER_SEAT,
} from './usageForm';
import { isUsageSwitchLockedOff } from './usageGate';

const safeDate = (d) => {
  if (!d) return null;
  const parsed = new Date(d);
  return Number.isNaN(parsed.getTime()) ? null : format(parsed, 'yyyy-MM-dd');
};

const toForm = (d) => {
  const base = d || computeUsageDefaults();
  return {
    start_date: base.start_date,
    expiration_date: base.expiration_date,
    storage_gb: Number(base.storage_gb) || 0,
    traffic_gb: Number(base.traffic_gb) || 0,
    email_quota: Number(base.email_quota) || 0,
    ai_quota_nt: Number(base.ai_quota_nt) || 0,
    activate: !!base.activate,
  };
};

/**
 * 企業共用配額（儲存空間 + 下載流量 + Email 總數量 + 企業 AI 用量）設定面板。
 *
 * 一組企業層級、全微服務共用的總額；帶生效期間與啟用開關，不區分試用、不可多筆。
 * 讀取由父層在開啟「管理方案」時抓一次、以 usageData prop 傳入。
 * 儲存空間 / 下載流量：使用者調整席位或啟停服務時（seatNonce 遞增）自動帶入
 * 「啟用席位 × 係數」的建議值，但仍可手動覆寫；label 顯示目前換算的建議數字。
 * Email 總數量與企業 AI 用量：每企業一份的月配額，與席位數無關（定價以級距計費），
 * 因此不跟著 seatNonce 連動、也沒有建議值，純人工填寫。
 * 企業 AI 用量存的是金額上限（NT$／月），由中控自行填寫，**不做級距選單**，
 * label 也不列 PRD 的標準方案金額 —— 任何數字都可以填，列出來只會看起來像限制。
 * 存金額而非級距代號，才容得下合約議定的金額（定價規則 §8 合約優先）。
 * 本面板不單獨存檔：以 reportUsage 把目前 payload / 合法性 / 變更旗標回報給彈窗，
 * 由彈窗「全部儲存」與各服務一起批次送出。
 * 不變式「任一服務啟用中 ⟹ 用量管理必須啟用」：anyServiceActive 為彈窗依各卡片當前
 * （未儲存）狀態算出的旗標，為 true 時鎖住停用方向，避免存出服務啟用但用量停用的狀態。
 */
const UsagePanel = ({
  usageData,
  activeSeatTotal = 0,
  seatNonce = 0,
  reportUsage,
  anyServiceActive = false,
}) => {
  const { auth, permissions } = useSelector(selectPermissionSettings);

  const canManage =
    auth?.roles?.find((it) => it.includes('workspace.admin')) ||
    permissions?.includes('tdp.services.enterprise.activate_services_enterprise_bought.center') ||
    permissions?.includes('tdp.services.enterprise.stop_services_enterprise_bought.center');
  const readOnly = !canManage;

  const [form, setForm] = useState(() => toForm(usageData));
  const [initial, setInitial] = useState(() => toForm(usageData));
  const [errors, setErrors] = useState({});
  // 面板展開／收合：點標題列可切換。預設依「是否已啟用」決定（啟用者展開）。
  const [expanded, setExpanded] = useState(() => !!usageData?.activate);

  // 只同步父層帶入的 usageData（開啟彈窗或存檔後重載）。
  useEffect(() => {
    const next = toForm(usageData);
    setForm(next);
    setInitial(next);
    setErrors({});
    setExpanded(!!next.activate);
  }, [usageData]);

  // 依「啟用中服務席位總和」換算的建議配額（顯示於 label；使用者可覆寫實際值）。
  const suggested = computeUsageQuota(activeSeatTotal);

  // 使用者調整席位 / 啟停服務時（seatNonce 遞增）自動帶入建議配額；之後仍可手動覆寫。
  // 以 ref 記住上次處理的 nonce，避免開窗初次載入（nonce 未變）誤觸而變 dirty。
  // 只覆寫 storage_gb / traffic_gb —— email_quota / ai_quota_nt 不隨席位連動，
  // 使用者填的值要留著。
  const seatNonceRef = useRef(seatNonce);
  useEffect(() => {
    if (seatNonce === seatNonceRef.current) return;
    seatNonceRef.current = seatNonce;
    setForm((prev) => ({
      ...prev,
      storage_gb: suggested.storage_gb,
      traffic_gb: suggested.traffic_gb,
    }));
    setErrors({});
  }, [seatNonce, suggested.storage_gb, suggested.traffic_gb]);

  const isDirty = isUsageDirty(form, initial);

  // 只鎖「停用」方向：已停用時仍可開啟（含修正存量的服務啟用但用量停用資料）。
  const lockedByServices = isUsageSwitchLockedOff({
    anyServiceActive,
    activate: form.activate,
  });

  // 回報目前 usage payload / 合法性 / 變更旗標給彈窗（供「全部儲存」批次送出）。
  useEffect(() => {
    if (!reportUsage) return;
    const payload = {
      start_date: safeDate(form.start_date),
      expiration_date: safeDate(form.expiration_date),
      storage_gb: Number(form.storage_gb) || 0,
      traffic_gb: Number(form.traffic_gb) || 0,
      email_quota: Number(form.email_quota) || 0,
      ai_quota_nt: Number(form.ai_quota_nt) || 0,
      activate: form.activate,
    };
    const validation = validateUsage(form);
    reportUsage({ payload, valid: validation.ok, errors: validation.errors, dirty: isDirty });
  }, [reportUsage, form, isDirty]);

  const patch = (partial) => {
    setForm((prev) => ({ ...prev, ...partial }));
    setErrors({});
  };

  return (
    <div className={styles.trialPanel} style={{ display: 'block' }}>
      {/* 點標題列可展開／收合；開關 stopPropagation 不觸發收合 */}
      <div
        className={styles.trialPanelTitle}
        style={{ cursor: 'pointer' }}
        onClick={() => setExpanded((e) => !e)}
      >
        <span onClick={(e) => e.stopPropagation()} style={{ display: 'inline-flex' }}>
          {lockedByServices && !readOnly ? (
            <Tips
              detail='請先停用所有服務，才能停用「用量管理」'
              side='top'
              align='start'
              topDiff={6}
            >
              <AlertSwitch
                disabled
                isChecked={form.activate}
                onChange={() => {}}
                color={{ on: '#5856D6', off: '#5856D64D' }}
              />
            </Tips>
          ) : (
            <AlertSwitch
              disabled={readOnly}
              isChecked={form.activate}
              onChange={() => {
                // 開啟用量管理時一併展開，讓使用者看見下方設定
                if (!form.activate) setExpanded(true);
                patch({ activate: !form.activate });
              }}
              color={{ on: '#5856D6', off: '#5856D64D' }}
            />
          )}
        </span>
        用量管理
        <span className={styles.planCardChevron} style={{ marginLeft: 'auto' }}>
          {expanded ? <MdKeyboardArrowDown size={18} /> : <MdKeyboardArrowRight size={18} />}
        </span>
      </div>

      {/* 點標題列展開才顯示下半部設定（與 PlanCard 一致，與啟用開關解耦） */}
      {expanded && (
        <div style={{ marginTop: '16px' }}>
          <div className={styles.trialPanelField}>
            <label>使用期間</label>
            <div className={styles.trialPanelDates}>
              <Datepicker
                selectDate={form.start_date}
                setSelectDate={(e) => {
                  if (readOnly) return;
                  patch({ start_date: e });
                }}
                disabled={readOnly}
                error={!!errors.start_date}
              />
              <span>至</span>
              <Datepicker
                selectDate={form.expiration_date}
                setSelectDate={(e) => {
                  if (readOnly) return;
                  patch({ expiration_date: e });
                }}
                disabled={readOnly}
                error={!!errors.expiration_date}
              />
            </div>
          </div>

          <div className={styles.trialPanelField}>
            <label>
              儲存空間容量（建議 {suggested.storage_gb} GB＝總啟用席位 {activeSeatTotal} 人 ×{' '}
              {STORAGE_GB_PER_SEAT} GB）
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <TextField
                placeholder='請輸入'
                width='100%'
                numeric
                value={form.storage_gb}
                onChange={(val) => patch({ storage_gb: val === '' ? '' : Number(val) })}
                error={!!errors.storage_gb}
                min={0}
                isDisabled={readOnly}
              />
              <span>GB</span>
            </div>
          </div>

          <div className={styles.trialPanelField}>
            <label>
              下載流量（建議 {suggested.traffic_gb} GB/月＝總啟用席位 {activeSeatTotal} 人 ×{' '}
              {TRAFFIC_GB_PER_SEAT} GB/月）
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <TextField
                placeholder='請輸入'
                width='100%'
                numeric
                value={form.traffic_gb}
                onChange={(val) => patch({ traffic_gb: val === '' ? '' : Number(val) })}
                error={!!errors.traffic_gb}
                min={0}
                isDisabled={readOnly}
              />
              <span style={{ whiteSpace: 'nowrap' }}>GB / 月</span>
            </div>
          </div>

          <div className={styles.trialPanelField}>
            <label>Email 總數量</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <TextField
                placeholder='請輸入'
                width='100%'
                numeric
                value={form.email_quota}
                onChange={(val) => patch({ email_quota: val === '' ? '' : Number(val) })}
                error={!!errors.email_quota}
                min={0}
                isDisabled={readOnly}
              />
              <span style={{ whiteSpace: 'nowrap' }}>封 / 月</span>
            </div>
          </div>

          <div className={styles.trialPanelField}>
            <label>企業 AI 用量</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <TextField
                placeholder='請輸入'
                width='100%'
                numeric
                value={form.ai_quota_nt}
                onChange={(val) => patch({ ai_quota_nt: val === '' ? '' : Number(val) })}
                error={!!errors.ai_quota_nt}
                min={0}
                isDisabled={readOnly}
              />
              <span style={{ whiteSpace: 'nowrap' }}>NT$ / 月</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsagePanel;
